import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ViewChildren, QueryList } from '@angular/core';
import { NgModel } from '@angular/forms';
import { MatDialog } from '@angular/material';
import { Router } from '@angular/router';
import { ConfirmDialogModel, DialogComponent } from 'src/app/AppBaseComponents/dialog/dialog.component';
import { ExecutionContainerComponent } from 'src/app/AppBaseComponents/execution-container/execution-container.component';
import { ActionModel } from 'src/app/Models/action-model';
import { ExecutionConfigModel } from 'src/app/Models/execution-config-model';
import { TestAutomationCase } from 'src/app/Models/test-automation-case.model';
import { ActionServiceService } from 'src/app/Services/action-service.service';
import { NotifyService } from 'src/app/Services/notify.service';
import { ParameterService } from 'src/app/Services/parameter.service';
import { RestService } from 'src/app/Services/rest.service';
import { SharedService } from 'src/app/Services/shared.service';
import { SuiteService } from 'src/app/Services/suite.service';
import { TestAutomationService } from 'src/app/Services/test-automation.service';
import { Utilities } from 'src/app/utilities';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'ze-test-cases-view',
  templateUrl: './test-cases-view.component.html',
  styleUrls: ['./test-cases-view.component.scss']
})
export class TestCasesViewComponent implements OnInit {

  @Output() reportSelected = new EventEmitter<any>();
  @ViewChild('categoryInput', { static: false }) categoryInput!: ElementRef<HTMLInputElement>;
  @ViewChild('tcid', { static: false }) testCaseIdField!: NgModel;
  @ViewChildren('stepInputRef') stepInputs!: QueryList<ElementRef>;

  selectedProjectId;
  testAutomationCases: TestAutomationCase[] = [];

  testAutomationId?: string | { $oid: string };
  suitesList: Array<{ name: string; value: string }> = [];

  loading = false;
  error = false;
  errorMessage = '';

  editRow: number | null = null;
  editableRow: TestAutomationCase = {} as TestAutomationCase;
  isNewRow: boolean = false;

  selectedTestData: any[] = [];
  public paramters: any[] = [];
  public isParametersAvailable: boolean = false;

  selectedIds: Set<string> = new Set();
  categoryList: string[] = [];
  filteredCategoryList: string[] = [];

  severityList = [
    { value: 'CRITICAL', name: 'CRITICAL' },
    { value: 'HIGH', name: 'HIGH' },
    { value: 'MEDIUM', name: 'MEDIUM' },
    { value: 'LOW', name: 'LOW' }
  ];

  constructor(private testAutomationService: TestAutomationService,
    private suiteService: SuiteService,
    private parameterService: ParameterService,
    private actionService: ActionServiceService,
    private restService: RestService,
    private http: HttpClient,
    private notifyService: NotifyService,
    private sharedService: SharedService,
    private router: Router,
    private dialog: MatDialog) {
    this.selectedProjectId = localStorage.getItem('projectId');
  }

  ngOnInit() {
    if (this.selectedProjectId) {
      this.loading = true;
      this.loadSuitesList(false);
    }
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }

  refreshCategoryList(index: number) {
    this.loadProjectCategoriesList(true, index);
  }

  filterCategories(value: string) {
    const filterValue = value.toLowerCase();
    this.filteredCategoryList = this.categoryList.filter(
      c => c.toLowerCase().includes(filterValue) &&
        !this.editableRow.category.includes(c)
    );
  }

  addTypedCategory(value: string) {
    const trimmed = value.trim();
    if (!trimmed) return;

    // Add to row chips
    if (!this.editableRow.category.includes(trimmed)) {
      this.editableRow.category.push(trimmed);
    }
    this.clearCategoryInput();
  }

  // Select from autocomplete
  selectCategory(value: string) {
    if (!this.editableRow.category.includes(value)) {
      this.editableRow.category.push(value);
    }
    this.clearCategoryInput();
  }

  clearCategoryInput() {
    if (this.categoryInput) {
      this.categoryInput.nativeElement.value = '';
    }
  }

  // Remove chip
  removeCategory(index: number, value: string) {
    this.editableRow.category.splice(index, 1);
    this.filterCategories(value);
  }

  refreshSuiteList() {
    this.loadSuitesList(true); // re-fetch suites
  }

  loadSuitesList(isRefreshSuiteList: boolean) {
    this.suiteService.getItems().subscribe({
      next: (response: any) => {
        const suites = response.data || [];

        this.suitesList = suites.map((item: any) => ({
          name: item.name,
          value: item._id
        }));

        if (!isRefreshSuiteList) {
          this.loadProjectTestCasesData();
        }
      },
      error: () => {
        console.error("Failed to load suite list");
        this.suitesList = [];
        if (!isRefreshSuiteList) {
          this.loadProjectTestCasesData();
        }
      }
    });
  }

  getSuiteName(id: string) {
    const found = this.suitesList.find(s => s.value == id);
    return found ? found.name : null;
  }

  loadProjectTestCasesData() {
    this.loading = true;
    this.errorMessage = '';
    this.error = false;
    this.testAutomationCases = [];
    this.testAutomationService.getProjectTestCasesData(this.selectedProjectId)
      .subscribe({
        next: (response: any) => {
          // response = JSON.parse(response);
          if (response.data && Array.isArray(response.data.test_cases) && response.data.test_cases.length > 0) {
            this.testAutomationCases = response.data.test_cases;
            this.testAutomationId = response.data._id;
          }
          this.loading = false;
        },
        error: (error) => {
          // Network/HTTP error - show error
          this.error = true;
          this.errorMessage = 'Unable to load Test Automation Cases';
          this.testAutomationCases = [];
          this.loading = false;
          console.error('Network error loading Test Automation Cases:', error);
        }
      });
  }

  addTestCase() {
    const newRow: TestAutomationCase = {
      test_case_id: '',
      description: '',
      test_steps: [],
      test_data: [],
      project_id: this.selectedProjectId,
      category: []
    };
    this.testAutomationCases.unshift(newRow);
    this.isNewRow = true;
    this.editRow = 0;
    this.editableRow = JSON.parse(JSON.stringify(this.testAutomationCases[0]));
  }

  loadProjectCategoriesList(isRefreshCategoryList: boolean, index: number) {
    this.testAutomationService.getProjectTestCaseCategories(this.selectedProjectId).subscribe({
      next: (response: any) => {
        this.categoryList = response.data || [];

        if (!isRefreshCategoryList) {
          this.openRowEdit(index);
        }
      },
      error: () => {
        console.error("Failed to load categories list");
        this.categoryList = [];
        if (!isRefreshCategoryList) {
          this.openRowEdit(index);
        }
      }
    });
  }

  edit(index: number) {
    if (this.editRow !== null) return;
    this.sharedService.loaderHide.emit(true);
    this.loadProjectCategoriesList(false, index);
  }

  openRowEdit(index: number) {
    if (this.testAutomationCases[index].category == undefined || this.testAutomationCases[index].category == null) {
      this.testAutomationCases[index].category = [];
    }
    this.editRow = index;
    this.editableRow = JSON.parse(JSON.stringify(this.testAutomationCases[index]));
    this.filterCategories("");
    this.sharedService.loaderHide.emit(false);
  }

  saveTestCaseCategories() {
    if (this.editableRow.category != null && this.editableRow.category != undefined && this.editableRow.category.length > 0) {
      this.testAutomationService.saveProjectTestCaseCategories(this.selectedProjectId, this.editableRow.category)
        .subscribe({
          next: (response) => {
            if (response.operationStatus === 'SUCCESS') {
              console.log('Project categories updated');
            } else {
              console.log("Error in adding project categories");
            }
          },
          error: (error) => {
            console.error('Error in adding project categories:', error);
          }
        });
    }
  }

  saveNewTestCase(index: number) {
    let testCase: TestAutomationCase = JSON.parse(JSON.stringify(this.editableRow));
    const testAutomationStringId = this.extractTestCaseId(this.testAutomationId!);
    if (testAutomationStringId || this.selectedProjectId) {
      this.saveTestCaseCategories();
      this.testAutomationService.addNewTestCase(testAutomationStringId, testCase)
        .subscribe({
          next: (response) => {
            console.log('addTestCase response:', response);
            if (response.operationStatus === 'SUCCESS') {
              this.testAutomationCases = [];
              this.editRow = null;
              this.loadProjectTestCasesData();
            } else {
              this.editRow = null;
              this.testAutomationCases.splice(0, 1);
              console.log("Error in adding testcase");
            }
          },
          error: (error) => {
            this.editRow = null;
            this.testAutomationCases.splice(0, 1);
            console.error('addTestCase error:', error);
          }
        });
    }
  }

  updateTestCase(index: number) {
    // Validate test_case_id
    if (this.editableRow.test_case_id != undefined && this.editableRow.test_case_id != null && this.editableRow.test_case_id == "") {
      this.testCaseIdField.control.markAsTouched();
      this.testCaseIdField.control.markAsDirty();
      this.notifyService.notify('warning', 'Warning', "Test Case ID is mandatory");
      return;
    }

    if (this.isNewRow) {
      this.saveNewTestCase(0);
      this.isNewRow = false;
    } else {
      let updatedRow: TestAutomationCase = JSON.parse(JSON.stringify(this.editableRow));

      if (updatedRow._id && typeof updatedRow._id === 'object' && '$oid' in updatedRow._id) {
        updatedRow._id = updatedRow._id.$oid;
      }
      this.saveTestCaseCategories();
      this.testAutomationService.updateTestCase(updatedRow)
        .subscribe({
          next: (response) => {
            console.log('updateTestCase response:', response);
            if (response.operationStatus === 'SUCCESS') {
              this.testAutomationCases[index] = JSON.parse(JSON.stringify(this.editableRow));
            } else {
              console.log("Error in updating testcase");
            }
          },
          error: (error) => {
            console.error('updateTestCase error:', error);
          }
        });
      this.editRow = null;
    }
  }

  deleteTestCase(index: number) {
    let testCase: TestAutomationCase = JSON.parse(JSON.stringify(this.testAutomationCases[index]));
    const testCaseId = this.extractTestCaseId(testCase._id!);
    const testAutomationStringId = this.extractTestCaseId(this.testAutomationId!);
    if ((testAutomationStringId || this.selectedProjectId) && testCaseId) {
      this.testAutomationService.deleteTestCase(testAutomationStringId, testCaseId)
        .subscribe({
          next: (response) => {
            console.log('deleteTestCase response:', response);
            if (response.operationStatus === 'SUCCESS') {
              this.testAutomationCases.splice(index, 1)
            } else {
              console.log("Error in deleteing testcase");
            }
          },
          error: (error) => {
            console.error('deleteTestCase error:', error);
          }
        });
    }
  }

  cancelEdit() {
    if (this.isNewRow) {
      this.isNewRow = false;
      this.testAutomationCases.splice(0, 1);
    }
    this.editableRow = {} as TestAutomationCase;
    this.editRow = null;
  }

  dropTestStep(event: CdkDragDrop<string[]>) {
    if (this.editableRow && this.editableRow.test_steps) {
      moveItemInArray(this.editableRow.test_steps, event.previousIndex, event.currentIndex);
    }
  }

  addStep() {
    if (this.editableRow && this.editableRow.test_steps) {
      this.editableRow.test_steps.push('');
    }
    setTimeout(() => {
      const inputs = this.stepInputs.toArray();
      const lastInput = inputs[inputs.length - 1];

      if (lastInput) {
        lastInput.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });

        lastInput.nativeElement.focus();
      }
    }, 50);
  }

  deleteStep(index: number) {
    if (this.editableRow && this.editableRow.test_steps) {
      this.editableRow.test_steps.splice(index, 1);
    }
  }

  private extractTestCaseId(testCaseId: string | { $oid: string }): string | null {
    if (typeof testCaseId === 'string') {
      return testCaseId;
    } else if (testCaseId && typeof testCaseId === 'object' && testCaseId.$oid) {
      return testCaseId.$oid;
    }

    return null;
  }

  runTest(row: any) {
    let executionConfig = new ExecutionConfigModel();
    executionConfig.resolution = "default_resolution";
    executionConfig.browser = ["Chrome"];
    executionConfig.default_pause = "0";
    executionConfig.screenshot_required = false;
    executionConfig.default_hub = true;
    executionConfig.local_execution = false;
    executionConfig.hub_ip = environment.selenoidHubIp;
    executionConfig.port = environment.selenoidHubPort;
    // Open ExecutionContainer dialog
    const dialogRef = this.dialog.open(ExecutionContainerComponent, {
      data: {
        executionConfig: row.execution_config ? row.execution_config : executionConfig,
        // testCaseId: row._id,
        testCaseId: this.extractTestCaseId(row._id),
        datasetLink: row.dataset_link ? row.dataset_link : "",
        dropped_suite: {
          _type: 'Suite',
          // name: row.suite_name,
          name: this.getSuiteName(row.suite_id),
          _id: row.suite_id
        }
      },
      backdropClass: 'blur-dialog-backdrop'
    });

    dialogRef.afterClosed().subscribe(result => {
      // add code to handle after close execution container pop-up
    });
  }

  openReport(row: TestAutomationCase) {
    this.router.navigate(['/report'], { queryParams: { reportId: row.last_run_report_id } });
  }

  getAlertMessage() {
    let tempMessage = "";
    let mySet1 = new Set();

    this.paramters.forEach(element => {
      for (const ele of element.commandSteps) {
        if ((ele.target && ((ele.target.deleted && ele.target.deleted == 1) || (ele.target.warnings && Object.keys(ele.target.warnings).length > 0))) ||
          (ele.target2 && ((ele.target2.deleted && ele.target2.deleted == 1) || (ele.target2.warnings && Object.keys(ele.target2.warnings).length > 0)))) {
          mySet1.add(element.name);
          break;
        }
      }
    });
    for (const value of mySet1) {
      tempMessage += "\n * " + value + "\n";
    }
    return tempMessage;
  }

  containsParameterPattern(attributes: any, PLACEHOLDER_PATTERN: any): boolean {
    if (attributes == undefined || attributes == null || !Array.isArray(attributes)) return false;

    return attributes.some(attr => {
      if (attr.disabled === true) return false;
      if (PLACEHOLDER_PATTERN.test(attr.key || '')) return true;

      if (attr.type === 'file') {
        // For 'file', check the src array
        if (Array.isArray(attr.src)) {
          return attr.src.some((s: string) => PLACEHOLDER_PATTERN.test(s));
        }
        return false;
      }
      return PLACEHOLDER_PATTERN.test(attr.value || '');
    });
  }

  updateParameterPresentInTarget(target) {
    if (Boolean(target.dom_id) && target.dom_id.startsWith("@@")) {
      this.isParametersAvailable = true;
    }
    if (Boolean(target.dom_name) && target.dom_name.startsWith("@@")) {
      this.isParametersAvailable = true;
    }
    if (Boolean(target.tag) && target.tag.startsWith("@@")) {
      this.isParametersAvailable = true;
    }
    if (Boolean(target.css) && target.css.startsWith("@@")) {
      this.isParametersAvailable = true;
    }
    if (Boolean(target.class) && target.class.startsWith("@@")) {
      this.isParametersAvailable = true;
    }
    if (Boolean(target.desc) && target.desc.startsWith("@@")) {
      this.isParametersAvailable = true;
    }
    if (Boolean(target.link) && target.link.startsWith("@@")) {
      this.isParametersAvailable = true;
    }
    if (Boolean(target.partial_link) && target.partial_link.startsWith("@@")) {
      this.isParametersAvailable = true;
    }
    if (Boolean(target.horizontal_index) && target.horizontal_index.startsWith("@@")) {
      this.isParametersAvailable = true;
    }
    if (target.xpaths && target.xpaths.length > 0) {
      for (const xpath of target.xpaths) {
        if (Boolean(xpath) && xpath.startsWith("@@")) {
          this.isParametersAvailable = true;
          break;
        }
      }
    }
  }

  getParamList(actionItemsList: ActionModel[]): { sheet1: any[]; sheet2: any[] } {
    let sheet1 = [];   // for params outside loop
    let sheet2 = [];   // for params inside loop
    let mainlist = [];
    let suiteHasHashInVar = false;

    let loopDepth = 0;

    for (let i = 0; i < this.paramters.length; i++) {
      for (let j = 0; j < this.paramters[i].commandSteps.length; j++) {
        let step = this.paramters[i].commandSteps[j];
        if (step.active === true || step.active === undefined) {
          let actionName = this.getActionNameById(actionItemsList, step.command.$oid);

          // Detect entering a loop
          if (actionName.includes("loop")) {
            loopDepth++;
          }
          let currentList = loopDepth > 0 ? sheet2 : sheet1;  // decide where to store

          if (this.paramters[i].commandSteps[j].type == "API") {
            const PLACEHOLDER_PATTERN = /\{\{@@[^}]+}}/g;

            // check in auth
            const auth = this.paramters[i].commandSteps[j].target.request.auth;
            if (auth && auth.type) {
              const authType = auth.type;
              const authAttrs = auth[authType];
              const authParameters = this.extractParametersFromAttributes(authAttrs, PLACEHOLDER_PATTERN);
              authParameters.forEach(parameter => {
                suiteHasHashInVar = true;
                const cleanParam = parameter.slice(2, -2); // removes '{{' and '}}'
                currentList.push(this.paramters[i].name + cleanParam);
              });
            }

            // check in headers
            const headers = this.paramters[i].commandSteps[j].target.request.headers;
            if (headers) {
              const headerParameters = this.extractParametersFromAttributes(headers, PLACEHOLDER_PATTERN);
              headerParameters.forEach(parameter => {
                suiteHasHashInVar = true;
                const cleanParam = parameter.slice(2, -2); // removes '{{' and '}}'
                currentList.push(this.paramters[i].name + cleanParam);
              });
            }

            // check in URL
            const urlRaw = this.paramters[i].commandSteps[j].target.request.url.raw;
            const urlParameters = this.extractParametersFromString(urlRaw, PLACEHOLDER_PATTERN);
            urlParameters.forEach(parameter => {
              suiteHasHashInVar = true;
              const cleanParam = parameter.slice(2, -2); // removes '{{' and '}}'
              currentList.push(this.paramters[i].name + cleanParam);
            });

            // check in request body
            const body = this.paramters[i].commandSteps[j].target.request.body;
            if (body.mode) {
              const bodyMode = body.mode;
              switch (bodyMode) {
                case "raw":
                  const rawBodyParameters = this.extractParametersFromString(body.raw, PLACEHOLDER_PATTERN);
                  rawBodyParameters.forEach(parameter => {
                    suiteHasHashInVar = true;
                    const cleanParam = parameter.slice(2, -2); // removes '{{' and '}}'
                    currentList.push(this.paramters[i].name + cleanParam);
                  });
                  break;
                case "formdata":
                  const formDataBodyParameters = this.extractParametersFromAttributes(body.formdata, PLACEHOLDER_PATTERN);
                  formDataBodyParameters.forEach(parameter => {
                    suiteHasHashInVar = true;
                    const cleanParam = parameter.slice(2, -2); // removes '{{' and '}}'
                    currentList.push(this.paramters[i].name + cleanParam);
                  });
                  break;
                case "urlencoded":
                  const urlencodedBodyParameters = this.extractParametersFromAttributes(body.urlencoded, PLACEHOLDER_PATTERN);
                  urlencodedBodyParameters.forEach(parameter => {
                    suiteHasHashInVar = true;
                    const cleanParam = parameter.slice(2, -2); // removes '{{' and '}}'
                    currentList.push(this.paramters[i].name + cleanParam);
                  });
                  break;
                case "file":
                  const fileBodyParameters = this.extractParametersFromString(body.file.src, PLACEHOLDER_PATTERN);
                  fileBodyParameters.forEach(parameter => {
                    suiteHasHashInVar = true;
                    const cleanParam = parameter.slice(2, -2); // removes '{{' and '}}'
                    currentList.push(this.paramters[i].name + cleanParam);
                  });
                  break;
                case "graphql":
                  const gqlQueryBodyParameters = this.extractParametersFromString(body.graphql.query, PLACEHOLDER_PATTERN);
                  gqlQueryBodyParameters.forEach(parameter => {
                    suiteHasHashInVar = true;
                    const cleanParam = parameter.slice(2, -2); // removes '{{' and '}}'
                    currentList.push(this.paramters[i].name + cleanParam);
                  });
                  const gqlVarsBodyParameters = this.extractParametersFromString(body.graphql.variables, PLACEHOLDER_PATTERN);
                  gqlVarsBodyParameters.forEach(parameter => {
                    suiteHasHashInVar = true;
                    const cleanParam = parameter.slice(2, -2); // removes '{{' and '}}'
                    currentList.push(this.paramters[i].name + cleanParam);
                  });
                  break;
              }
            }

          } else {
            let value = this.paramters[i].commandSteps[j].value;
            let value2 = this.paramters[i].commandSteps[j].value2;
            if (Boolean(value) && value.startsWith("@@")) {
              suiteHasHashInVar = true;
              currentList.push(this.paramters[i].name + value);
            }
            if (Boolean(value2) && value2.startsWith("@@")) {
              suiteHasHashInVar = true;
              currentList.push(this.paramters[i].name + value2);
            }
            if (this.paramters[i].commandSteps[j].target) {
              suiteHasHashInVar = this.addParameterInTargetToList(this.paramters[i].commandSteps[j].target, currentList, this.paramters[i].name) ? true : suiteHasHashInVar;
            }
            if (this.paramters[i].commandSteps[j].target2) {
              suiteHasHashInVar = this.addParameterInTargetToList(this.paramters[i].commandSteps[j].target2, currentList, this.paramters[i].name) ? true : suiteHasHashInVar;
            }

            if (Boolean(value))
              mainlist.push(this.paramters[i].name + "@@" + value);
            if (Boolean(value2))
              mainlist.push(this.paramters[i].name + "@@" + value2)
          }

          if (actionName.includes("close statement")) {
            if (loopDepth > 0) loopDepth--; // only close if inside loop
          }
        }
      }
    }
    sheet1 = [...new Set(sheet1)];
    sheet2 = [...new Set(sheet2)];
    // return suiteHasHashInVar ? { sheet1, sheet2 } : mainlist;
    return { sheet1, sheet2 };
  }

  addParameterInTargetToList(target, list, stepName): boolean {
    let suiteHasHashInVar = false;
    if (Boolean(target.dom_id) && target.dom_id.startsWith("@@")) {
      suiteHasHashInVar = true;
      list.push(stepName + target.dom_id);
    }
    if (Boolean(target.dom_name) && target.dom_name.startsWith("@@")) {
      suiteHasHashInVar = true;
      list.push(stepName + target.dom_name);
    }
    if (Boolean(target.tag) && target.tag.startsWith("@@")) {
      suiteHasHashInVar = true;
      list.push(stepName + target.tag);
    }
    if (Boolean(target.css) && target.css.startsWith("@@")) {
      suiteHasHashInVar = true;
      list.push(stepName + target.css);
    }
    if (Boolean(target.class) && target.class.startsWith("@@")) {
      suiteHasHashInVar = true;
      list.push(stepName + target.class);
    }
    if (Boolean(target.desc) && target.desc.startsWith("@@")) {
      suiteHasHashInVar = true;
      list.push(stepName + target.desc);
    }
    if (Boolean(target.link) && target.link.startsWith("@@")) {
      suiteHasHashInVar = true;
      list.push(stepName + target.link);
    }
    if (Boolean(target.partial_link) && target.partial_link.startsWith("@@")) {
      suiteHasHashInVar = true;
      list.push(stepName + target.partial_link);
    }
    if (Boolean(target.horizontal_index) && target.horizontal_index.startsWith("@@")) {
      suiteHasHashInVar = true;
      list.push(stepName + target.horizontal_index);
    }
    if (target.xpaths && target.xpaths.length > 0) {
      for (const xpath of target.xpaths) {
        if (Boolean(xpath) && xpath.startsWith("@@")) {
          suiteHasHashInVar = true;
          list.push(stepName + xpath);
        }
      }
    }
    return suiteHasHashInVar;
  }

  getActionNameById(actionItemsList: ActionModel[], actionId) {
    const action = actionItemsList.find(item => (item._id["$oid"] ? item._id["$oid"] : item._id) === actionId);
    return action ? action.name : "";
  }

  extractParametersFromString(input: string, pattern: any): string[] {
    if (!input || typeof input !== 'string') return [];
    const matches = input.match(pattern);
    return matches ? matches : [];
  }

  extractParametersFromAttributes(attributes: any[], pattern: any): string[] {
    if (attributes == undefined || attributes == null || !Array.isArray(attributes)) return [];

    const results: string[] = [];

    for (const attr of attributes) {
      if (attr.disabled === true) continue;

      // Always check key
      results.push(...this.extractParametersFromString(attr.key, pattern));

      if (attr.type === 'file' && Array.isArray(attr.src)) {
        for (const src of attr.src) {
          results.push(...this.extractParametersFromString(src, pattern));
        }
      } else {
        results.push(...this.extractParametersFromString(attr.value, pattern));
      }
    }

    return results;
  }

  downloadTemplate(row: TestAutomationCase) {
    if (!row.suite_id) {
      console.warn('No suite_id available for download');
      return;
    }
    this.parameterService.findBySuiteId(row.suite_id, 1).subscribe((data) => {
      this.paramters = data.data;
      let message = this.getAlertMessage();
      if (message != "") {
        let warning_message = Utilities.EXECUTION_WARNING_MESSAGE + "\n";
        alert(warning_message + message);
      } else {
        for (let i = 0; i < this.paramters.length; i++) {
          for (let j = 0; j < this.paramters[i].commandSteps.length; j++) {
            if (this.paramters[i].commandSteps[j].active !== false) {
              if (this.paramters[i].commandSteps[j].type == "API") {
                const PLACEHOLDER_PATTERN = /\{\{@@[^}]+}}/;
                let isAuthParametersAvailable = false;
                let isHeadersParametersAvailable = false;
                let isUrlParametersAvailable = false;
                let isBodyParametersAvailable = false;
                // check in authentication
                if (this.paramters[i].commandSteps[j].target.request.auth
                  && this.paramters[i].commandSteps[j].target.request.auth.type) {
                  let authType = this.paramters[i].commandSteps[j].target.request.auth.type;
                  isAuthParametersAvailable = this.containsParameterPattern(this.paramters[i].commandSteps[j].target.request.auth[authType], PLACEHOLDER_PATTERN);
                }

                // check in headers
                if (this.paramters[i].commandSteps[j].target.request.headers) {
                  isHeadersParametersAvailable = this.containsParameterPattern(this.paramters[i].commandSteps[j].target.request.headers, PLACEHOLDER_PATTERN);
                }

                // check in url
                isUrlParametersAvailable = PLACEHOLDER_PATTERN.test(this.paramters[i].commandSteps[j].target.request.url.raw);

                // check in request body
                if (this.paramters[i].commandSteps[j].target.request.body
                  && this.paramters[i].commandSteps[j].target.request.body.mode) {
                  const bodyMode = this.paramters[i].commandSteps[j].target.request.body.mode;
                  switch (bodyMode) {
                    case "raw":
                      isBodyParametersAvailable = PLACEHOLDER_PATTERN.test(this.paramters[i].commandSteps[j].target.request.body.raw);
                      break;
                    case "formdata":
                      isBodyParametersAvailable = this.containsParameterPattern(this.paramters[i].commandSteps[j].target.request.body.formdata, PLACEHOLDER_PATTERN);
                      break;
                    case "urlencoded":
                      isBodyParametersAvailable = this.containsParameterPattern(this.paramters[i].commandSteps[j].target.request.body.urlencoded, PLACEHOLDER_PATTERN);
                      break;
                    case "file":
                      isBodyParametersAvailable = PLACEHOLDER_PATTERN.test(this.paramters[i].commandSteps[j].target.request.body.file.src);
                      break;
                    case "graphql":
                      isBodyParametersAvailable = PLACEHOLDER_PATTERN.test(this.paramters[i].commandSteps[j].target.request.body.graphql.query) || PLACEHOLDER_PATTERN.test(this.paramters[i].commandSteps[j].target.request.body.graphql.variables);
                      break;
                  }
                }
                if (isAuthParametersAvailable || isBodyParametersAvailable || isHeadersParametersAvailable || isUrlParametersAvailable) {
                  this.isParametersAvailable = true;
                }
              } else {
                let value = this.paramters[i].commandSteps[j].value;
                let value2 = this.paramters[i].commandSteps[j].value2;
                if (Boolean(value) && value.startsWith("@@")) {
                  this.isParametersAvailable = true;
                }
                if (Boolean(value2) && value2.startsWith("@@")) {
                  this.isParametersAvailable = true;
                }
                if (this.paramters[i].commandSteps[j].target) {
                  this.updateParameterPresentInTarget(this.paramters[i].commandSteps[j].target);
                }
                if (this.paramters[i].commandSteps[j].target2) {
                  this.updateParameterPresentInTarget(this.paramters[i].commandSteps[j].target2);
                }
              }
            }
          }
        }
        const suiteName = this.getSuiteName(row.suite_id) || "suite";
        if (this.isParametersAvailable) {
          this.actionService.getItems().subscribe((data) => {
            let actionItemsList: ActionModel[] = data['data'];
            let { sheet1, sheet2 } = this.getParamList(actionItemsList);

            // Remove duplicates
            let nonLoop = sheet1.filter((value, index, array) => array.indexOf(value) === index);
            let loop = sheet2.filter((value, index, array) => array.indexOf(value) === index);

            // Headers
            const headers: string[] = [...nonLoop, ...loop];

            // First row: mark each column as Loop or NonLoop
            const typeRow: string[] = [
              ...nonLoop.map(() => "NonLoop"),
              ...loop.map(() => "Loop")
            ];

            // Build CSV (first row = loop/nonloop, second row = headers)
            let csvString = typeRow.join(",") + "\r\n" + headers.join(",") + "\r\n";

            // Download CSV
            csvString = "data:text/csv;charset=utf-8," + encodeURIComponent(csvString);
            const downloadLink = document.createElement("a");
            downloadLink.setAttribute("href", csvString);
            downloadLink.setAttribute("download", suiteName + ".csv");
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
          });
        } else {
          // Download CSV
          let csvString = "data:text/csv;charset=utf-8," + encodeURIComponent("");
          const downloadLink = document.createElement("a");
          downloadLink.setAttribute("href", csvString);
          downloadLink.setAttribute("download", suiteName + ".csv");
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
        }
      }
    });
  }

  downloadDataset(row: TestAutomationCase) {
    // Validate test case id
    const testCaseId = this.extractTestCaseId(row._id!);
    if (!testCaseId) {
      console.error('No valid testCaseId available for dataset download');
      return;
    }

    this.http.get(this.restService.apis.testCaseDatasetDownload.url + testCaseId, {
      responseType: 'blob'
    }).subscribe(data => {
      const blob = new Blob([data], { type: 'application/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'dataset' + '.csv';
      document.body.appendChild(a);
      a.click();
    },
      error => {
        if (error.status === 404) {
          this.notifyService.notify('error', 'Error', "Dataset does not exists or Deleted");
        } else {
          this.notifyService.notify('error', 'Error', "Error in downloading dataset");
        }
      }
    );

  }

  onFileSelected(event: any, row: TestAutomationCase, index: number, fileInput: any) {
    const file: File = event.target.files[0];

    if (!file) {
      return;
    }

    fileInput.value = "";

    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      alert("Please upload a CSV file");
      return;
    }

    this.testAutomationService.uploadTestcaseCsvFile(file, this.extractTestCaseId(row._id)).subscribe({
      next: (res) => {
        if (res.operationStatus !== 'ERROR') {
          this.testAutomationCases[index].dataset_link = this.extractTestCaseId(row._id);
          this.notifyService.notify('success', 'Success', "File uploaded successfully!");
        } else {
          this.notifyService.notify('error', 'Error', "Upload failed!");
        }

      },
      error: (err) => {
        this.notifyService.notify('error', 'Error', "Upload failed!");
        console.error(err);
      }
    });
  }

  getRowId(row: TestAutomationCase): string | null {
    return this.extractTestCaseId(row._id);
  }

  isSelected(row: TestAutomationCase): boolean {
    const id = this.getRowId(row);
    return !!id && this.selectedIds.has(id);
  }

  isIndeterminate(): boolean {
    const selectableCount = this.testAutomationCases.reduce((c, r) => c + (this.getRowId(r) ? 1 : 0), 0);
    return this.selectedIds.size > 0 && this.selectedIds.size < selectableCount;
  }

  toggleSelection(row: TestAutomationCase, checked: boolean) {
    const id = this.getRowId(row);
    if (!id) return; // checkbox should be disabled already for this case
    if (checked) {
      this.selectedIds.add(id);
    } else {
      this.selectedIds.delete(id);
    }
  }

  /** Toggle select-all: only selects rows that have an _id (persisted rows) */
  toggleSelectAll(checked: boolean) {
    if (checked) {
      // add all selectable ids
      this.testAutomationCases.forEach(row => {
        const id = this.getRowId(row);
        if (id) this.selectedIds.add(id);
      });
    } else {
      // clear all selections
      this.selectedIds.clear();
    }
  }

  isAllSelected(): boolean {
    const selectableCount = this.testAutomationCases.reduce((c, r) => c + (this.getRowId(r) ? 1 : 0), 0);
    return selectableCount > 0 && this.selectedIds.size === selectableCount;
  }

  /** Returns true if ANY testcase in the list has an _id (persisted) */
  hasAnyPersistedTestcase(): boolean {
    return this.testAutomationCases.some(r => !!this.getRowId(r));
  }

  /** Collect selected full testcase objects (useful for payloads) */
  getSelectedTestCases(): TestAutomationCase[] {
    const ids = new Set(this.selectedIds); // snapshot
    return this.testAutomationCases.filter(r => {
      const id = this.getRowId(r);
      return !!id && ids.has(id);
    });
  }

  /** Build a payload suitable for your backend (example) */
  buildPayloadForRun(): any {
    // example returns array of minimal objects; adapt as needed by backend
    return this.getSelectedTestCases().map(tc => ({
      _id: this.getRowId(tc),
      test_case_id: tc.test_case_id,
      project_id: tc.project_id,
      suite_id: tc.suite_id,
      description: tc.description,
      // include other fields you need
      execution_config: tc.execution_config || null,
      dataset_link: tc.dataset_link || null
    }));
  }

  /** Called when user clicks Run Selected */
  runSelectedTestCases() {
    const selected = this.getSelectedTestCases();
    if (selected.length === 0) return;

    const message = `Run ${selected.length} testcases?`;
    const dialogData = new ConfirmDialogModel("", message);
    const dialogRef = this.dialog.open(DialogComponent, {
      data: dialogData
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        this.loading = true;
        const ids = selected.map(tc => this.getRowId(tc));

        this.testAutomationService.runSelectedTestCases(null, ids).subscribe({
          next: (res: any) => {
            if (res && res.operationStatus === 'SUCCESS') {
              // this.selectedIds.clear();
              this.notifyService.notify('success', 'Triggered', 'Selected testcases triggered.');
            } else {
              const unTriggeredCount = res.data.length;
              this.notifyService.notify('error', 'Trigger Error', 'Could not trigger ' + unTriggeredCount + ' selected testcases.');
            }
            this.loadProjectTestCasesData();
          },
          error: (err) => {
            console.error('runSelected error', err);
            this.loading = false;
            this.notifyService.notify('error', 'Trigger Error', 'Error running selected testcases.');
          }
        });

      }
    });
  }

  /** Called when user clicks Delete Selected */
  deleteSelectedTestCases() {
    const selected = this.getSelectedTestCases();
    if (selected.length === 0) return;

    const message = `Are you sure you want to delete ${selected.length} testcases?`;
    const dialogData = new ConfirmDialogModel("Delete !!!", message);
    const dialogRef = this.dialog.open(DialogComponent, {
      data: dialogData
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        this.loading = true;

        const ids = selected.map(tc => this.getRowId(tc)).filter((id): id is string => id !== null);

        if (ids.length === 0) {
          this.loading = false;
          this.notifyService.notify('error', 'Delete Error', 'No valid test case IDs found for deletion.');
          return;
        }

        // Check if using DB2 (if projectIdDb2 exists in localStorage)
        const isDB2 = !!localStorage.getItem('projectIdDb2');

        // Create payload
        const payload = {
          ids: ids,
          isDB2: isDB2
        };

        this.testAutomationService.deleteSelectedTestCases(payload).subscribe({
          next: (res: any) => {
            if (res && res.operationStatus === 'SUCCESS') {
              this.selectedIds.clear();
              const deletedCount = res.data.split(" ")[0];
              this.notifyService.notify('success', 'Delete', deletedCount + ' selected testcases deleted.');
            } else {
              this.notifyService.notify('error', 'Delete Error', 'Could not delete selected testcases.');
            }
            this.loadProjectTestCasesData();
          },
          error: (err) => {
            console.error('deleteSelected error', err);
            this.loading = false;
            this.notifyService.notify('error', 'Delete Error', 'Error deleting selected testcases.');
          }
        });
      }
    });
  }

}
